import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Location } from '@angular/common';
import { distinctUntilChanged, filter } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableSchemaModel } from '@models/admin/schemaModels/table-schema.model';
import { CardSchemaModel } from '@models/admin/schemaModels/card-schema.model';
import { FormSchemaModel } from '@models/admin/schemaModels/form-schema.model';
import { AdminSectionModel } from '@models/admin/admin-section.model';
import { DashboardSchemaModel } from '@models/admin/schemaModels/dashboard-schema.model';

@Injectable()
export class AdminApiService {
  private readonly destroy$ = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);

  tableSchema: WritableSignal<TableSchemaModel | null> = signal(null);
  cardSchema: WritableSignal<CardSchemaModel | null> = signal(null);
  formSchema: WritableSignal<FormSchemaModel | null> = signal(null);
  dashboardSchema: WritableSignal<DashboardSchemaModel | null> = signal(null);
  modalSchema: WritableSignal<CardSchemaModel | null> = signal(null);
  modalFormSchema: WritableSignal<FormSchemaModel | null> = signal(null);

  methods: WritableSignal<AdminSectionModel[]> = signal([]);
  gotMethods = computed(() => this.methods().length > 0);

  updateOnRedirect = false;

  private id!: string | number;
  private schemaName!: string;

  constructor() {
    effect(() => {
      if (!this.methods().length) {
        this.getAdminMethods();
      }
    });

    this.location.subscribe(() => {
      this.updateOnRedirect = true;
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroy$),
      distinctUntilChanged((prev: NavigationEnd, curr: NavigationEnd) => {
        return prev.urlAfterRedirects.split('?')[0] === curr.urlAfterRedirects.split('?')[0];
      }),
    )
      .subscribe(() => {
        if (location.pathname.includes('admin')) {
          this.updateOnRedirect = false;
          this.clearSchemas();
          this.getSchema();
        }
      });

    this.activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this.destroy$),
    )
      .subscribe((params) => {
        this.id = params['id'];
        this.schemaName = params['schemaName'];
      });
  }

  clearSchemas() {
    this.tableSchema.set(null);
    this.cardSchema.set(null);
    this.formSchema.set(null);
    this.dashboardSchema.set(null);
  }

  getSchema(schemaType?: string, schemaName?: string) {
    const currentSection = this.methods().find((method) => this.router.url.includes(method.routeLink));
    if (!currentSection?.tableSection && !schemaType)
      return;

    if (!schemaName)
      schemaName = this.schemaName && schemaType ? this.schemaName : this.getSchemaName();

    if (schemaName) {
      if (!schemaType)
        schemaType = 'table';

      const requestBody = { schemaName, schemaType, params: {} };
      if (this.id && schemaType != 'table')
        requestBody.params['id'] = this.id;
      this.http.post('/schema/get', requestBody)
        .subscribe(
          (data) => {
            this.updateSchema(schemaType, data);
          });
    }
  }

  getModalSchema(url: string) {
    const schemaType = 'card';

    const schemaName = this.getSchemaName(url.split('?')[0]);
    const id = url.split('=')[1];

    const requestBody = { schemaName, schemaType, params: { id } };

    this.http.post('/schema/get', requestBody)
      .subscribe(
        (data) => {
          if (data[schemaType])
            this.modalSchema.set(data[schemaType]);
        });
  }

  getModalFormSchema(schemaName: string, params: any) {
    const schemaType = 'form';

    const requestBody = { schemaName, schemaType, params };

    this.http.post('/schema/get', requestBody)
      .subscribe(
        (data) => {
          this.modalFormSchema.set(data[schemaType]);
        });
  }

  updateSchema(schemaType: string, data: any) {
    if (schemaType == 'table')
      this.tableSchema.set(data[schemaType]);
    else if (schemaType == 'card')
      this.cardSchema.set(data[schemaType]);
    else if (schemaType == 'form')
      this.formSchema.set(data[schemaType]);
    else if (schemaType == 'dashboard')
      this.dashboardSchema.set(data[schemaType]);
  }

  getSchemaName(name?: string) {
    let method = name ?
      this.methods().find((method) => name.includes(method.routeLink)) :
      this.methods().find((method) => this.router.url.includes(method.routeLink));

    return method ? method.routeLink : '';
  }

  getAdminMethods() {
    this.http.post<any>('/schema/info', {})
      .subscribe(
        (data) => {
          this.methods.set(data?.schemas);
          if (this.methods().length) {
            this.getSchema();
          }
        });
  }
}
